/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import '@/assets/styles/index.scss';

import Vuex from 'vuex';
import { Dialog } from 'vant';
import {
  Component, ProvideReactive, Vue, Watch,
} from 'vue-property-decorator';

import { debounce, throttle } from 'lodash';
import { namespace } from 'vuex-class';
import NavBar, { OldNavBarRef } from '@/components/nav-bar';
import CoffeeNavBar from '@/components/nav-bar-coffee/index.vue';
import {
  setPageTitle, setNativeNavBar, setStatusBar,
} from '@/api/native/utils';
import VueRouter, { Route, RouteRecord } from 'vue-router';
import { getAndStoreMaxQuota } from '@/shared/utils/getMaxQuota';
import { initializeDuplicatePrevent, destroyDuplicatePrevent } from '@/hooks/useDuplicatePrevent';
import { updateModuleState } from './store';
import LoginUtil from './viewsH5/login/LoginUtils';
import { getIfIsTestUser } from './api/user';
import isPrerendering, { isDeprecationPrerendering } from './shared/utils/isPrerendering';
import { isPro } from './constant';
import platform from './shared/utils/platform';
import { RouterV2Enum } from './router/router-names';
import { GlobalEventEnum } from './enums/global-event';

import { NavBarCustomProps } from './components/nav-bar/type';

import '@/assets/styles/theme.variable.scss';
import '@/assets/styles/theme.variable.blue.scss';
import '@/assets/styles/theme.variable.orange.scss';
import '@/assets/styles/van.blue.scss';

Vue.use(Dialog);
Vue.use(Vuex);
Vue.use(VueRouter);

const BaseStore = namespace('base');
const _isPrerendering = isPrerendering();
const _isDeprecationPrerendering = isDeprecationPrerendering();

const htmlElement = document.querySelector('html');
const hashPath = window.location.hash.replace(/(^#|\/advert$|(\/advert)?\?[^$]*)/g, '');

let initialRoute: string | null = '';

if (_isPrerendering) {
  initialRoute = hashPath;
} else {
  initialRoute = htmlElement ? htmlElement.getAttribute('initial-route') : '';
}

const getScrollTop = (): number => document.documentElement.scrollTop || document.body.scrollTop;
const getClientHeight = (): number => document.documentElement.clientHeight
  || document.body.clientHeight;

@Component({
  components: {
    NavBar,
  },
})
export default class App extends Vue {
  @BaseStore.State isLogoutUser!: boolean;

  @BaseStore.State progress!: string;

  @BaseStore.State forceUpdateKey!: string;

  @BaseStore.Action $updateState!: (data: Dictionary) => void;

  // 控制H5 NavBar 的显隐
  @ProvideReactive() isShowH5Nav = false;

  @ProvideReactive() isH5NavInitialized = false;

  // 当前键盘是否可见
  private isKeyboardAppear = false;

  // 键盘弹出前的scrollTop，方便键盘收起之后恢复最初的滚动
  private scrollTopBeforeKeyboardAppear = 0;

  // 初始化屏幕可视区域高度
  private originClientHeight = getClientHeight();

  // 标记是否是因为切换导航引起的Resize
  private isWindowResizedByNavChange = false;

  // 自定义navbar属性样式
  private navBarCustomProps: NavBarCustomProps = {
    showCenterTitle: false,
  };

  private isShowCoffeeH5Nav = false;

  /** 是否自定义coffee h5导航栏 */
  private isControlCoffeeH5Nav = false;

  // 向全局分发键盘弹出或者收起事件
  @Watch('isKeyboardAppear')
  private onIsKeyboardAppearChange() {
    if (!this.isKeyboardAppear) {
      // 回弹到键盘出现之前的位置
      window.scrollTo(0, this.scrollTopBeforeKeyboardAppear);
    }

    const keyboardEvent = document.createEvent('HTMLEvents');

    keyboardEvent.initEvent(
      `keyboard-${this.isKeyboardAppear ? 'appear' : 'disappear'}`,
      true,
    );

    document.dispatchEvent(keyboardEvent);
  }


  private previousNavStatus:boolean|undefined = undefined;

  private get controlNavPageSelf() {
    const controlNavPageList = [RouterV2Enum.myProfile, RouterV2Enum.accountTypeInfo];
    return controlNavPageList.includes(this.$route.path as RouterV2Enum);
  }

  /** 是否显示老h5导航栏 */
  private get showOldH5Nav() {
    return this.isShowH5Nav && !this.isShowCoffeeH5Nav && !this.isControlCoffeeH5Nav;
  }

  /** 是否显示公共coffee h5导航栏 */
  private get showCoffeeH5Nav() {
    return this.isShowH5Nav && this.isShowCoffeeH5Nav && !this.isControlCoffeeH5Nav;
  }

  /** 配置了coffee h5导航栏（用于新增一套app页面布局,h5与app内布局需要保持一致） */
  private get configCoffeeH5Nav() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { isShowH5Nav } = this.$route.meta || {};
    return isShowH5Nav && (this.isShowCoffeeH5Nav || this.isControlCoffeeH5Nav);
  }

  // 路由切换之后重置数据状态
  @Watch('$route', { immediate: true })
  private onRouteChange(to:Route, from:RouteRecord) {
    this.isKeyboardAppear = false;
    this.scrollTopBeforeKeyboardAppear = 0;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { isShowH5Nav } = (from && from.meta) || {};
    this.previousNavStatus = isShowH5Nav as boolean;
    this.getH5NavStatus();
  }

  /** 对当前的h5导航栏存在时的状态栏做展示判断 */
  private setH5StatusNav(status:boolean) {
    if ((platform.isAndroid || platform.isHarmony) && platform.isHelloApp) {
      if (this.$route.meta.controlNav) {
        setStatusBar({ isShowStatusBar: false });
      } else {
        setStatusBar({ isShowStatusBar: status });
      }
    }
  }

  private getH5NavStatus() {
    this.isWindowResizedByNavChange = false;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const {
      navBarCustomProps,
      isShowCoffeeH5Nav = false,
      isControlCoffeeH5Nav = false,
    } = this.$route.meta || {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.navBarCustomProps = navBarCustomProps;
    this.isShowCoffeeH5Nav = isShowCoffeeH5Nav as boolean;
    this.isControlCoffeeH5Nav = isControlCoffeeH5Nav as boolean;

    if (platform.isHelloApp && !isPrerendering() && Object.keys(this.$route.meta).length !== 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const temp = !!this.$route.meta.isShowH5Nav;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { isShowH5Nav: metaShowH5Nav } = this.$route.meta || {};

      if (this.isShowH5Nav !== temp) {
        // 修正第一次打开页面，不是navChange引起的resize事件
        // 情况1：不是首次打开的路由切换修正
        // 情况2：首次打开但是需要显示h5导航栏的情况下，resize异步需要获得最终的clientHeight高度
        if (this.isH5NavInitialized
          || (!this.isH5NavInitialized && metaShowH5Nav !== undefined)) {
          this.setH5StatusNav(!temp);
          this.isWindowResizedByNavChange = true;
        }
        this.isShowH5Nav = temp;
        this.isH5NavInitialized = true;
      } else if (metaShowH5Nav !== this.previousNavStatus) {
        this.setH5StatusNav(!temp);
        // 情况3: 从原生导航栏切换到h5导航栏的情况
        this.isWindowResizedByNavChange = true;
        this.isShowH5Nav = temp;
        this.isH5NavInitialized = true;
      }
      // h5导航栏展示时统一在此处处理原生状态栏隐藏
      setNativeNavBar(!this.isShowH5Nav);
    }
    this.originClientHeight = getClientHeight();
  }


  private async created() {
    this.adjustTargetRoute = throttle(this.adjustTargetRoute.bind(this), 100);

    // 非预渲染环境，且需要显示的目标路径与当前路径不一致，则进行替换
    // 主要考虑history.back返回的时候，一般android会重新刷新页面，ios不会
    if (!_isPrerendering) {
      // 获取项目额度灰度结果
      getAndStoreMaxQuota();
      // 兼容会刷新页面的情况；
      if (initialRoute && initialRoute !== hashPath) {
        this.adjustTargetRoute(initialRoute);
      }

      // 不会刷新的情况，通过pageshow等来控制
      if (window.performance && window.performance.navigation.type === 2) {
        if (platform.isHelloIosApp) {
          this.getH5NavStatus();
        }
      } else {
        window.addEventListener('pageshow', (event) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { title = '臻有钱', isShowH5Nav } = this.$route.meta || {};
          /** 解决小程序环境 || APP环境下使用h5导航栏，跨项目回退到当前应用标题不更新问题 */
          if (platform.isAlipayMini || (platform.isHelloIosApp && !isShowH5Nav)) {
            setPageTitle(title);
            // 需要限制在app内，支付宝小程序下使用会报错
            if (platform.isHelloApp) {
              setNativeNavBar(true);
            }
          }
          if (event.persisted) {
            if (platform.isHelloIosApp) {
              this.getH5NavStatus();
            }
          }
        });
      }
    }

    const isSupportWebpValue = localStorage.getItem('AppFinanceH5Web_isSupportWebp') === 'true';
    if (isSupportWebpValue) {
      document.documentElement.classList.add('webp');
      this.$updateState({
        isSupportWebp: true,
      });
    } else if (await this.isSupportWebp()) {
      localStorage.setItem('AppFinanceH5Web_isSupportWebp', 'true');
      document.documentElement.classList.add('webp');
      this.$updateState({
        isSupportWebp: true,
      });
    }

    if (platform.isAndroid || platform.isHarmony) {
      document.documentElement.classList.add('is-android');
    }
  }

  private adjustTargetRoute(_initialRoute: string) {
    this.$router.replace(_initialRoute);
  }

  private render() {
    const initialRouteList = Object.keys(window.__PRERENDER_SOURCE_MAP || {});

    const appProps: Dictionary = {
      class: [
        !this.controlNavPageSelf && !this.configCoffeeH5Nav && 'app',
        // 配置了coffee h5导航栏时，设置对应的app样式，避免影响原本的布局
        this.configCoffeeH5Nav && 'coffee-nav-app',
      ],
      attrs: {
        ...this.$attrs,
        id: 'app',
        // 保障所有非预渲染路由挂载在default节点上，避免被隐藏掉
        'initial-route': _isDeprecationPrerendering || (initialRoute && initialRouteList.includes(initialRoute))
          ? initialRoute
          : 'default',
      },
    };

    if (_isPrerendering) {
      (appProps.attrs as Dictionary)['data-server-rendered'] = true;
    }

    const {
      leftColor, color, background, textAlign, statusBarBackground, isDarkStatusBar, fontSize,
      isShowAndroidStatusBar, showCenterTitle, title = '',
    } = this.navBarCustomProps || {};

    return (
      // 显示coffee组件时不需要app的@include safe-padding-top();样式，因为组件内部已经处理了
      <div
        {...appProps}
      >
        <div class={[
          'app-content',
          !_isPrerendering && this.showOldH5Nav && !this.controlNavPageSelf && 'app-content-padding',
          !_isPrerendering && platform.isHelloApp && this.showOldH5Nav && !this.controlNavPageSelf && platform.isAndroid && 'nav-content-padding',
        ]}>
          <div class="app-nav-bar">
          {this.showOldH5Nav && (
            <NavBar
              ref="oldNavBar"
              leftColor={leftColor}
              color={color}
              background={background}
              textAlign={textAlign}
              showCenterTitle={showCenterTitle}
              title={title}
              statusBarBackground={statusBarBackground || 'white'}
              isShowStatusBar={isShowAndroidStatusBar || false}
              isDarkStatusBar={isDarkStatusBar || true}
              show={this.isShowH5Nav}
              key={this.progress}
              fontSize={fontSize}
            >
              {this.progress && this.progress !== '100%' // 100%时 不显示文案
              && <span class='userProgress' slot="titleLeft">
                {this.progress}
              </span>
              }
              </NavBar>
          )}
          {this.showCoffeeH5Nav && <CoffeeNavBar/>}
          </div>
          <div class="app-content-view">
            {/* 现通过include && max 双重限制，保证keep-alive生效范围 */}
            <keep-alive include={['HomePageV3Component', 'ActivityHomeV3Component']} max={1}>
              <router-view key={`${String(this.$route.name || this.$route.path)}${this.forceUpdateKey}`} />
            </keep-alive>
          </div>
        </div>
      </div>
    );
  }

  // 判断当前环境是否支持webp
  private isSupportWebp() {
    return new Promise((resolve) => {
      const image = new Image();

      image.onload = () => {
        if (image.width && image.height) {
          resolve(true);
        }
        resolve(false);
      };
      image.onerror = () => resolve(false);
      image.src = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    });
  }

  private async mounted() {
    if (!_isPrerendering) {
      await this.injectDebugContext();

      // 🛡️ 防重复工具：初始化路由守卫，清理可中止任务
      initializeDuplicatePrevent();

      // debounce: 避免resize事件触发多次
      window.addEventListener('resize', debounce(this.onWindowResizeEvent.bind(this), 200));
      window.addEventListener('focusin', this.onInputFocusInEvent.bind(this));
      window.addEventListener('focusout', this.onInputFocusOutEvent.bind(this));
      // 监听路由meta变化，更新h5导航栏状态
      this.$root.$on(GlobalEventEnum.UPDATE_NAVBAR_STATUS, () => {
        this.getH5NavStatus();

        if (this.$refs.oldNavBar) {
          (this.$refs.oldNavBar as OldNavBarRef).forceUpdateNavBarImageUse();
        }
      });
    }
  }

  private beforeDestroy() {
    // 🛡️ 防重复工具：销毁实例，清理所有任务
    destroyDuplicatePrevent();

    window.removeEventListener('resize', this.onWindowResizeEvent.bind(this));
    window.removeEventListener('focusin', this.onInputFocusInEvent.bind(this));
    window.removeEventListener('focusout', this.onInputFocusOutEvent.bind(this));
    // 移除全局事件监听
    this.$root.$off(GlobalEventEnum.UPDATE_NAVBAR_STATUS);
  }

  private async injectDebugContext() {
    const isLogin = LoginUtil.isLogin();
    try {
      const isTestUser = isLogin && !this.isLogoutUser && (await getIfIsTestUser());

      if (isTestUser) {
        await updateModuleState('base', {
          isTestUser: true,
        });
      }

      if (!isPro || isTestUser) {
        // eslint-disable-next-line no-unused-expressions
        import('@/components/debug-tool');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  }

  private onWindowResizeEvent() {
    if (!this.isWindowResizedByNavChange) {
      if (getClientHeight() !== this.originClientHeight) {
        // 软键盘显示
        this.handleKeyboardEvent(true);
      } else {
        // 软键盘隐藏
        this.handleKeyboardEvent(false);
      }
    } else {
      this.isWindowResizedByNavChange = false;
      this.originClientHeight = getClientHeight();
    }
  }

  private onInputFocusEvent(isFocusIn: boolean, event: Event) {
    const targetElement = event.target as HTMLInputElement;
    const needCaptureEvent = targetElement
      && ['[object HTMLInputElement]', '[object HTMLTextAreaElement]'].includes(
        targetElement.toString(),
      );

    if (
      needCaptureEvent
      && targetElement.getAttribute('readonly') !== 'readonly'
    ) {
      if (isFocusIn) {
        this.handleKeyboardAppearEvent();
      } else {
        this.handleKeyboardDisappearEvent();
      }
    }
  }

  private onInputFocusInEvent = this.onInputFocusEvent.bind(this, true);

  private onInputFocusOutEvent = this.onInputFocusEvent.bind(this, false);

  // 部分Android机型在键盘收起之后不会触发focusout事件，所以需要同时配合window.resize事件监听
  // 部分iOS机型在键盘收起的时候不会触发window.resize事件，所以需要配合focusout事件来监听
  // 所以需要同时监听focus**以及resize事件，并避免重复执行isKeyboardAppear的更新
  private handleKeyboardEvent(isAppearEvent: boolean) {
    if (isAppearEvent && !this.isKeyboardAppear) {
      this.isKeyboardAppear = true;
      this.scrollTopBeforeKeyboardAppear = getScrollTop();
    }

    if (!isAppearEvent && this.isKeyboardAppear) {
      this.isKeyboardAppear = false;
    }
  }

  private handleKeyboardAppearEvent = throttle(
    this.handleKeyboardEvent.bind(this, true),
    100,
  );

  private handleKeyboardDisappearEvent = throttle(
    this.handleKeyboardEvent.bind(this, false),
    100,
  );
}
